import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from "../../auth/service/auth.service";
import { OrganizationDTO } from "../dto/OrganizationDTO";
import { InitStateBusinessService } from "../../../core/service/init-state-business.service";
import { OrganizationWithAccesses } from "../../../core/component/hornavbar/dto/InitDataDTO";
import { FRONT_URLS } from "../../../util/common/url-parts";

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService {
  private authService = inject(AuthService);
  private router = inject(Router);
  private initService = inject(InitStateBusinessService);

  private allOrgsWithAccesses: OrganizationWithAccesses[];

  private _all = new BehaviorSubject<OrganizationDTO[]>(undefined);
  private _selected = new BehaviorSubject<OrganizationDTO>(undefined);
  private _selectedWithAccesses = new BehaviorSubject<OrganizationWithAccesses>(undefined);
  private _showCompanyCrudModal = new BehaviorSubject<boolean>(false);

  setAll(organizations: OrganizationWithAccesses[]): void {
    this.allOrgsWithAccesses = organizations;
    const organizationsWithoutRoles = organizations.map((organization) => organization.organization);
    this._all.next(organizationsWithoutRoles);
  }

  async setSelected(org: OrganizationDTO): Promise<void> {
    const currentData = this._selected.getValue();
    if (currentData && org === currentData) {
      return;
    }
    const orgWithAccesses = this.allOrgsWithAccesses.find((o) => o.organization.uuid === org.uuid);
    this.authService.setFmsAccessInsideOrganization(orgWithAccesses.userAccesses.fmsAccess);
    await this.router.navigate([], { queryParams: { o: org.uuid } });
    this._selected.next(org);
    this._selectedWithAccesses.next(orgWithAccesses);
  }

  get all$(): BehaviorSubject<OrganizationDTO[]> {
    return this._all;
  }

  get selected$(): BehaviorSubject<OrganizationDTO> {
    return this._selected;
  }

  get selectedWithAccesses$(): BehaviorSubject<OrganizationWithAccesses> {
    return this._selectedWithAccesses;
  }

  get allOrgsWithAccess(): OrganizationWithAccesses[] {
    return this.allOrgsWithAccesses;
  }

  get showCompanyCrudModal(): Observable<boolean> {
    return this._showCompanyCrudModal.asObservable();
  }

  setShowCompanyCrudModal(value: boolean): void {
    this._showCompanyCrudModal.next(value);
  }

  resetAllData(): void {
    this._all.next(null);
    this._selected.next(null);
    this._selectedWithAccesses.next(null);
  }

  async removeOrganization(orgUuid: string): Promise<void> {
    if (!this.allOrgsWithAccesses) {
      return;
    }

    this.allOrgsWithAccesses = this.allOrgsWithAccesses.filter(
      (orgWithAccess) => orgWithAccess.organization.uuid !== orgUuid
    );

    const organizationsWithoutRoles = this.allOrgsWithAccesses.map(
      (organization) => organization.organization
    );
    this._all.next(organizationsWithoutRoles);
    this._selected.next(null);
    this._selectedWithAccesses.next(null);
    if (this.allOrgsWithAccesses.length === 0) {
      this.resetAllData();
    }
    this.initService.refreshInitData();
    await this.router.navigate([FRONT_URLS.ENTRANCE]);
  }
}
